import { type ComponentPropsWithoutRef, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { numberFormatter } from "utils/formatters/number";
import { selectTotalPriceParts } from "store/quote/selectors";
import { useCountUp } from "react-countup";
import { Component, PriceAdornment, PriceParts } from "./styles";
import classNames from "classnames";

const REACT_COUNTUP_OPTIONS = {
  duration: 1,
  separator: numberFormatter(1000, {
    getParts: true,
    style: "currency",
  }).groupSeparator,
  start: 0,
  end: 0,
  startOnMount: false,
};

export interface PriceProps extends ComponentPropsWithoutRef<"div"> {
  startAdornment?: string;
  uniformHeight?: boolean;
}

export function Price({ startAdornment, uniformHeight }: PriceProps) {
  const totalPriceParts = useSelector(selectTotalPriceParts);
  const fractionRef = useRef<HTMLSpanElement | null>(null);
  const integerRef = useRef<HTMLSpanElement | null>(null);

  const { update: updateInteger } = useCountUp({
    ...REACT_COUNTUP_OPTIONS,
    ref: integerRef,
    start: totalPriceParts?.integer ? parseInt(totalPriceParts.integer) : 0,
  });

  const { update: updateFraction } = useCountUp({
    ...REACT_COUNTUP_OPTIONS,
    formattingFn: (value) => (value < 10 ? `0${value}` : `${value}`),
    ref: fractionRef,
    start: totalPriceParts?.fraction ? parseInt(totalPriceParts.fraction) : 0,
  });

  useEffect(() => {
    if (typeof totalPriceParts?.integer !== "undefined") {
      updateInteger(totalPriceParts.integer);
    }
  }, [totalPriceParts?.integer, updateInteger]);

  useEffect(() => {
    if (typeof totalPriceParts?.fraction !== "undefined") {
      updateFraction(totalPriceParts.fraction);
    }
  }, [totalPriceParts?.fraction, updateFraction]);

  return (
    <>
      {totalPriceParts && (
        <Component>
          {startAdornment && (
            <PriceAdornment
              className={classNames({ "uniform-height": uniformHeight })}
            >
              {startAdornment}
            </PriceAdornment>
          )}
          <PriceParts
            className={classNames({ "uniform-height": uniformHeight })}
          >
            <span className="currency">{totalPriceParts.currency}</span>
            <span ref={integerRef} />
            <span className="fraction">{totalPriceParts.decimalSeparator}</span>
            <span className="fraction" ref={fractionRef} />
          </PriceParts>
        </Component>
      )}
    </>
  );
}
